// src/pages/JMS/Managament/ManagementLayout.tsx
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
  matchPath,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { IcCheckmark } from '@/assets/images';
import {
  MuiStyles,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/TailwindComponents';
import { cn } from '@/components/TailwindComponents/lib/utils';
import { Header } from '@/pages/JMS/Managament/components/Header';
import { DeleteBookingDialog } from '@/pages/JMS/Managament/components/modals/DeleteBookingModal';
import { getTabGroups } from '@/pages/JMS/Managament/components/tabs/tabGroups';
import {
  TabName,
  TabStatus,
  useBookingStore,
} from '@/pages/JMS/store/BookingStore';

const ManagementLayout = () => {
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get('mode') === 'view';
  const isInmateContext = searchParams.get('context') === 'inmate';

  const location = useLocation();
  const navigate = useNavigate();

  const { booking } = useBookingStore();
  const bookingId = booking?._id;
  const inmateId = booking?.inmate?._id;
  const submittedData = booking?.submittedData;

  const pathSegments = location.pathname.split('/');
  const currentPath = pathSegments[pathSegments.length - 1];

  type TabConfig = {
    title: string;
    value: TabName;
    active: boolean;
    navigateTo: string;
    disabled?: boolean;
    hidden?: boolean;
    checkmark?: boolean;
  };

  type ConfigType = {
    headerTitle: string;
    tabs: TabConfig[];
  };

  const tabGroups = getTabGroups(
    bookingId,
    inmateId,
    currentPath,
    submittedData,
    isViewMode,
    isInmateContext,
  );

  const StatusIcon = ({ tabName }: { tabName: TabName }) => {
    if (submittedData && tabName in submittedData) {
      const status = submittedData[tabName]?.status;
      if (status === TabStatus.SUBMITTED) {
        return <IcCheckmark className="h-5 w-5 text-green-500" />;
      }
    }
    return null;
  };

  const getTabConfig = (): ConfigType => {
    const group = tabGroups.find(({ matchRoutes }) =>
      matchRoutes.some((pattern) => matchPath(pattern, location.pathname)),
    );

    return group?.config ?? { headerTitle: '', tabs: [] };
  };

  const config = getTabConfig();
  const activeTab = config.tabs.find((tab) => tab.active)?.title;

  const handleTabNavigation = (navigateTo: string) => {
    navigate(navigateTo);
  };

  return (
    <div className="flex flex-col gap-4 w-full h-full">
      <HelmetProvider>
        <Helmet>
          <title>{activeTab}</title>
        </Helmet>
      </HelmetProvider>
      <Header title={config.headerTitle} />
      {config.tabs.length ? (
        <Tabs value={activeTab}>
          <TabsList
            className={cn(
              'flex items-start justify-start gap-7 px-5',
              MuiStyles.TabsList,
            )}
          >
            {config.tabs.map(
              ({
                title,
                value,
                navigateTo,
                disabled,
                hidden,
                checkmark = true,
              }) =>
                !hidden && (
                  <div key={value} className="flex gap-1 h-full items-center">
                    <TabsTrigger
                      className={cn(MuiStyles.TabsTrigger)}
                      value={title}
                      onClick={() => handleTabNavigation(navigateTo)}
                      disabled={disabled && activeTab !== title}
                    >
                      {title}
                    </TabsTrigger>
                    {checkmark && <StatusIcon tabName={value} />}
                  </div>
                ),
            )}
          </TabsList>
        </Tabs>
      ) : null}
      <Outlet />
      <DeleteBookingDialog />
    </div>
  );
};

export default ManagementLayout;
